import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';

import {
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
  DeliveriesResponse,
} from './useConvenienceDispatchDeliveries.query';

const TRUCKS_QUERY_KEY = 'useQueryPlanningsTrucks';

type UpdateVehicleColorArgs = {
  scope: 'truck' | 'partner_company';
  color: string | null; // null = クリア（palette フォールバックへ戻す）
  truckId?: number;
  partnerCompanyId?: number;
};

/**
 * /map の車両カラー設定。PATCH /api/v5/vehicle_colors を叩く。
 *
 * レスポンス body は消費しない: API の render_json は camelize せず最小 echo に留まる
 * ため、サーバ確定値との整合は onSettled の invalidate → refetch に委ねる。
 *
 * 楽観更新: PATCH 完了を待たず onMutate でローカルキャッシュを即時書き換え、地図の
 * ルート線色と左リスト色バーを即反映する。表示色は deliveries(infinite) の truckColor から
 * 引く(mapVM/vehicleColorFor とも sourceRows=deliveries 由来)ため、deliveries の truckColor と
 * trucks の color を対象 truck 分だけ更新する。scope=partner_company の対象 truck は trucks
 * キャッシュから partnerCompanyId 一致で導出し、server のコピーダウンと同じ範囲をローカル再現する。
 */
export const useUpdateVehicleColorMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: UpdateVehicleColorArgs) => {
      await axios.patch('/api/v5/vehicle_colors', {
        data: {
          scope: args.scope,
          color: args.color,
          ...(args.truckId != null ? { truck_id: args.truckId } : {}),
          ...(args.partnerCompanyId != null ? { partner_company_id: args.partnerCompanyId } : {}),
        },
      });
    },

    onMutate: async (args: UpdateVehicleColorArgs) => {
      const deliveriesKey = [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY];
      const trucksKey = [TRUCKS_QUERY_KEY];

      // 進行中の refetch が楽観値を上書きしないよう先にキャンセルし、ロールバック用に snapshot。
      await queryClient.cancelQueries({ queryKey: deliveriesKey });
      await queryClient.cancelQueries({ queryKey: trucksKey });
      const prevDeliveries = queryClient.getQueriesData({ queryKey: deliveriesKey });
      const prevTrucks = queryClient.getQueriesData<PlanningsTruckEntity[]>({ queryKey: trucksKey });

      // 色を適用する truckId 集合を決める。truck scope は当該のみ、partner_company scope は
      // trucks キャッシュから partnerCompanyId 一致の truck を導出(server のコピーダウン範囲)。
      const affectedTruckIds = new Set<number>();
      if (args.scope === 'truck' && args.truckId != null) {
        affectedTruckIds.add(args.truckId);
      } else if (args.scope === 'partner_company' && args.partnerCompanyId != null) {
        prevTrucks.forEach(([, trucks]) => {
          trucks?.forEach((t) => {
            if (t.partnerCompanyId === args.partnerCompanyId) affectedTruckIds.add(t.id);
          });
        });
      }

      // deliveries(infinite): 対象 truck の delivery の truckColor を差し替える。
      queryClient.setQueriesData<InfiniteData<DeliveriesResponse>>({ queryKey: deliveriesKey }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((d) =>
              affectedTruckIds.has(d.truckId) ? { ...d, truckColor: args.color } : d,
            ),
          })),
        };
      });

      // trucks: 対象 truck の color を差し替える。partner_company scope なら partnerCompanyColor も
      // 揃えてピッカーの現在色表示と整合させる。
      queryClient.setQueriesData<PlanningsTruckEntity[]>({ queryKey: trucksKey }, (old) => {
        if (!old) return old;
        return old.map((t) => {
          if (!affectedTruckIds.has(t.id)) return t;
          return args.scope === 'partner_company'
            ? { ...t, color: args.color, partnerCompanyColor: args.color }
            : { ...t, color: args.color };
        });
      });

      return { prevDeliveries, prevTrucks };
    },

    // 失敗時は snapshot へロールバック。ユーザー通知(snackbar)は呼び出し側 mutate の onError で行う。
    onError: (_err, _args, context) => {
      context?.prevDeliveries.forEach(([key, data]) => queryClient.setQueryData(key, data));
      context?.prevTrucks.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },

    // 成否に関わらず最後に invalidate し、サーバ確定値へ整合(楽観値と一致すれば表示は無変化)。
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [TRUCKS_QUERY_KEY] });
    },
  });
};
